import React from 'react';
import { motion } from 'framer-motion';
import { Trophy, Target, Flame, Award } from 'lucide-react';

const DSASection = () => {
  const achievements = [
    {
      icon: <Trophy size={32} />,
      title: 'Problem Solving',
      description: 'Strong foundation in algorithms and data structures',
      color: '#f59e0b',
    },
    {
      icon: <Target size={32} />,
      title: 'Algorithmic Thinking',
      description: 'Efficient solution design for complex problems',
      color: '#3b82f6',
    },
    {
      icon: <Flame size={32} />,
      title: 'Active Practice',
      description: 'Regular coding challenges on LeetCode platform',
      color: '#ef4444',
    },
    {
      icon: <Award size={32} />,
      title: 'Code Optimization',
      description: 'Focus on time and space complexity optimization',
      color: '#10b981',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5 },
    },
  };

  return (
    <section id="dsa" className="dsa-section">
      <div className="section-container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="section-title">DSA Expertise</h2>
          <div className="title-underline"></div>
          <p className="section-subtitle">
            Mastering Data Structures & Algorithms for optimal problem-solving
          </p>
        </motion.div>

        <motion.div
          className="dsa-grid"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {achievements.map((achievement, index) => (
            <motion.div
              key={index}
              className="dsa-card"
              variants={itemVariants}
              whileHover={{ y: -8, transition: { duration: 0.3 } }}
            >
              <div
                className="dsa-icon"
                style={{
                  backgroundColor: `${achievement.color}20`,
                  color: achievement.color,
                }}
              >
                {achievement.icon}
              </div>
              <h3 className="dsa-title">{achievement.title}</h3>
              <p className="dsa-description">{achievement.description}</p>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          className="dsa-highlights"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <div className="highlight-box">
            <h4>Core Strengths</h4>
            <ul className="strengths-list">
              <li>Arrays, Strings & Hashing</li>
              <li>Linked Lists & Trees</li>
              <li>Dynamic Programming</li>
              <li>Graphs & BFS/DFS</li>
              <li>Sorting & Searching</li>
              <li>Recursion & Backtracking</li>
            </ul>
          </div>

          <div className="leetcode-link-box">
            <h4>Practice Platform</h4>
            <p>Active on LeetCode, continuously improving problem-solving skills</p>
            <a
              href="https://leetcode.com/u/Pranjal69/"
              target="_blank"
              rel="noopener noreferrer"
              className="leetcode-link"
            >
              View LeetCode Profile
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default DSASection;